/*
 * HLTH +Care cart-drawer upsell toggle.
 *
 * Optimistic UI:
 *   - Toggle, Total, Checkout-button price, and cart count update INSTANTLY
 *     on click — no waiting for the network.
 *   - /cart/add.js (add) and /cart/update.js keyed by variant ID (remove)
 *     fire in the background, serialized through a single promise chain so
 *     back-to-back clicks don't race. No drawer re-render on toggle, so it
 *     stays snappy.
 *   - The warranty line exists on the real cart (so Shopify charges for it
 *     at checkout) but is hidden visually via CSS.
 *
 * Checkout sync:
 *   - When the cart is "dirty" from a toggle, the Checkout submit is
 *     intercepted only long enough to let the toggle's pending
 *     /cart/add.js|update.js call finish, then the existing form is submitted
 *     natively (so pixels / apps hooking the submit event still fire). The
 *     warranty is an extra server cart line beyond the form's positional
 *     updates[], so a bare submit leaves it untouched — no re-render needed.
 *     (We intentionally do NOT re-render the whole cart-drawer section here;
 *     that server round trip was the main source of checkout lag.)
 *
 * Also handles the Learn-more popup.
 */
(function () {
  var CARD_SEL = '[data-hlth-care-card]';
  var TOGGLE_SEL = '[data-hlth-care-toggle]';

  function getToggles() {
    var card = document.querySelector(CARD_SEL);
    if (!card) return [];
    return Array.from(card.querySelectorAll(TOGGLE_SEL));
  }

  // ── Money helpers ─────────────────────────────────────────────────────
  function moneyFormatFor(text) {
    var m = /^([^\d-]*)([\d.,]+)(.*)$/.exec((text || '').trim());
    if (!m) return { prefix: '', suffix: '', cents: 0 };
    var digits = m[2].replace(/[^\d]/g, '');
    return {
      prefix: m[1] || '',
      suffix: m[3] || '',
      cents: digits ? parseInt(digits, 10) : 0,
    };
  }

  function formatMoney(cents, fmt) {
    var v = (cents / 100).toFixed(2);
    var parts = v.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.prefix + parts.join('.') + fmt.suffix;
  }

  function updateMoneyEl(el, deltaCents) {
    if (!el) return;
    var fmt = moneyFormatFor(el.textContent);
    var next = fmt.cents + deltaCents;
    if (next < 0) next = 0;
    el.textContent = formatMoney(next, fmt);
  }

  // ── Cart count badge ──────────────────────────────────────────────────
  function updateCountBadge(deltaItems) {
    var el = document.querySelector('.drawer__heading-count');
    if (!el) return;
    var m = /(\d+)/.exec(el.textContent || '');
    var current = m ? parseInt(m[1], 10) : 0;
    var next = current + deltaItems;
    if (next < 0) next = 0;
    el.textContent = '(' + next + ')';
  }

  // ── Background cart calls (serialized) ────────────────────────────────
  var queue = Promise.resolve();
  var inFlight = 0;

  function cartFetch(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (j) {
        return { ok: r.ok, body: j };
      });
    });
  }

  // Set true whenever a toggle modifies the cart via AJAX. While dirty, the
  // cart-drawer form's positional updates[] inputs no longer match the real
  // cart line count (we skip re-rendering on toggle for snappiness), so the
  // form must be re-synced before it can be submitted.
  var cartDirty = false;

  function enqueue(task) {
    inFlight++;
    queue = queue
      .then(task)
      .catch(function (err) {
        console.error('[hlth-care-toggle]', err);
      })
      .then(function () {
        inFlight--;
      });
    return queue;
  }

  // ── Checkout ──────────────────────────────────────────────────────────
  // The warranty is a normal cart line, so checkout goes through the normal
  // form submission. The cart form has `novalidate` (so a hidden warranty
  // row whose qty exceeds the variant's quantity_rule.max can't silently
  // block submission). If a toggle changed the cart since the last render,
  // the form's positional updates[] are stale, so we re-render the drawer
  // to re-sync the form, then submit it natively (preserving the submit
  // event for analytics pixels / apps).
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target && event.target.closest
        ? event.target.closest('#CartDrawer-Checkout')
        : null;
      if (!btn) return;

      // Cart already matches the form → let the native submit proceed.
      if (!cartDirty && inFlight === 0) return;

      event.preventDefault();
      // Instant feedback so the click doesn't feel dead during the sync.
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');

      // Only wait for the toggle's in-flight /cart/add.js|update.js to finish,
      // then submit the EXISTING form natively. We deliberately skip the old
      // full-section rerenderDrawer() round trip — re-rendering this large
      // cart-drawer section server-side was the slowest step on the checkout
      // path. The server cart is already correct once `queue` drains; the
      // warranty line is an extra server line beyond the form's positional
      // updates[], so a bare submit leaves it untouched. Submitting the form
      // (vs. window.location) preserves the `submit` event for pixels/apps.
      queue
        .then(function () {
          cartDirty = false;
          var form = document.getElementById('CartDrawer-Form');
          if (form && typeof form.requestSubmit === 'function') {
            form.requestSubmit(btn);
            return;
          }
          if (form) {
            form.submit();
            return;
          }
          window.location.href = '/checkout';
        })
        .catch(function () {
          window.location.href = '/checkout';
        });
    },
    true,
  );

  // ── Optimistic update applier ─────────────────────────────────────────
  function applyOptimistic(prevToggle, nextToggle) {
    // prevToggle: the warranty being removed (or null)
    // nextToggle: the warranty being added (or null)
    var prevCents = prevToggle ? parseInt(prevToggle.dataset.priceCents, 10) || 0 : 0;
    var prevQty = prevToggle ? parseInt(prevToggle.dataset.quantity, 10) || 0 : 0;
    var nextCents = nextToggle ? parseInt(nextToggle.dataset.priceCents, 10) || 0 : 0;
    var nextQty = nextToggle ? parseInt(nextToggle.dataset.quantity, 10) || 0 : 0;

    // "Was" (compare-at) contribution — falls back to the sale price when the
    // line isn't on sale, mirroring the footer's cart_compare_total math.
    var prevCompare = prevToggle
      ? parseInt(prevToggle.dataset.compareCents, 10) || prevCents
      : 0;
    var nextCompare = nextToggle
      ? parseInt(nextToggle.dataset.compareCents, 10) || nextCents
      : 0;

    var deltaCents = nextCents - prevCents;
    var deltaCompare = nextCompare - prevCompare;
    var deltaItems = nextQty - prevQty;

    updateMoneyEl(document.querySelector('.totals__total-now'), deltaCents);
    updateMoneyEl(document.querySelector('.cart__checkout-total'), deltaCents);
    // Keep the struck-through "was" total in sync too, so savings stay correct.
    updateMoneyEl(document.querySelector('.totals__total-was'), deltaCompare);
    updateCountBadge(deltaItems);
  }

  // Idempotent sync for a single toggle. Reads CURRENT state at execution
  // time, then issues the minimum API call needed to reconcile the server
  // cart with the toggle's checked state. Safe to run multiple times.
  //
  // Uses /cart/update.js keyed by variant ID for removals instead of
  // /cart/change.js keyed by line.key — variant IDs are stable across
  // drawer re-renders, line keys can go stale. Shopify treats
  // {updates: {variantId: 0}} as "remove if present, no-op otherwise".
  async function syncToggleToCart(toggle) {
    var desired = toggle.checked;
    var variantId = toggle.dataset.variantId;
    var inCart = toggle.dataset.inCart === 'true';

    if (desired && !inCart) {
      var qty = parseInt(toggle.dataset.quantity, 10) || 1;
      var res = await cartFetch('/cart/add.js', {
        items: [{ id: Number(variantId), quantity: qty }],
      });
      if (res.ok) toggle.dataset.inCart = 'true';
      cartDirty = true;
    } else if (!desired && inCart) {
      var updates = {};
      updates[variantId] = 0;
      await cartFetch('/cart/update.js', { updates: updates });
      // Whether the server returned 200 or an error, the toggle is OFF
      // and the variant shouldn't be in cart — flip the flag so we don't
      // keep retrying.
      toggle.dataset.inCart = '';
      cartDirty = true;
    }
  }

  // ── Auto-add Shipping Insurance on add-to-cart ────────────────────────
  // Behavior: whenever the +Care card renders with the Shipping Insurance row
  // and the variant isn't in the cart, add it via /cart/add.js + section
  // replace. This is intentionally non-opt-outable: if the shopper turns the
  // toggle off, the next drawer mutation re-adds it. The store wants Shipping
  // Insurance on every order.

  // Apply a freshly server-rendered `cart-drawer` section to the live DOM the
  // same way Dawn's onCartUpdate does (cart.js): replaceWith on `cart-drawer-items`
  // and `.cart-drawer__footer` — those are the two sibling regions inside
  // `<cart-drawer>` whose contents change when the cart changes. The drawer
  // header and `.cart__ctas` (checkout button) are not replaced here because
  // Dawn doesn't either; their server-rendered values stay in sync because the
  // whole `#CartDrawer` was already replaced via product-form's renderContents
  // step earlier in the ATC flow.
  function applyDrawerSection(sectionHtml) {
    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');

    // Mirror `<cart-drawer>`'s is-empty class from the fresh section — Liquid
    // sets it via `{% if cart == empty %}is-empty{% endif %}`, and the empty
    // state's CSS keys off it.
    var liveDrawer = document.querySelector('cart-drawer');
    var freshDrawer = doc.querySelector('cart-drawer');
    if (liveDrawer && freshDrawer) {
      liveDrawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
    }

    // Replace the entire `.drawer__inner` interior — same pattern Dawn uses
    // in CartDrawerItems.updateQuantity. This handles every transition
    // cleanly: items added/removed, qty changes, AND the non-empty→empty
    // case where Liquid only renders the `.drawer__inner-empty` block inside
    // `.drawer__inner`. Targeting only `cart-drawer-items` + `.cart-drawer__footer`
    // (as Dawn's onCartUpdate does) left `.drawer__inner-empty` missing on
    // that transition, which showed up as a blank-white drawer when the
    // shopper emptied their cart.
    //
    // The drawer's `#CartDrawer-Overlay` and its click-to-close listener live
    // OUTSIDE `.drawer__inner` so they're untouched. Custom elements inside
    // (`cart-drawer-items`, `quantity-popover`, etc.) get re-instantiated
    // when their nodes are re-inserted, which re-runs their connectedCallback
    // bindings — same lifecycle Dawn's own pattern relies on.
    var liveInner = document.querySelector('.drawer__inner');
    var freshInner = doc.querySelector('.drawer__inner');
    if (liveInner && freshInner) {
      liveInner.innerHTML = freshInner.innerHTML;
    }
  }

  // Single in-flight guard. /cart/add.js is not idempotent — calling it twice
  // for the same variant adds qty=2. Cleared in .finally so a future ATC cycle
  // (e.g. cart emptied → ATC again) can re-trigger.
  var autoAddInFlight = false;

  function performShippingAutoAdd() {
    if (autoAddInFlight) return;
    var toggle = document.querySelector(
      '[data-hlth-care-toggle][data-hlth-care-type="shipping"]'
    );
    if (!toggle) return;
    // Already in cart (per the latest server-rendered state) — nothing to do.
    if (toggle.dataset.inCart === 'true') return;

    var variantId = Number(toggle.dataset.variantId);
    var qty = parseInt(toggle.dataset.quantity, 10) || 1;
    if (!variantId) return;

    autoAddInFlight = true;
    cartDirty = true;
    // Check the actual server cart before adding. Dawn's onCartUpdate can
    // land a stale section render *after* our successful add (Dawn's request
    // was sent with the pre-shipping cart state), which wipes our applied
    // section and leaves the DOM saying data-in-cart="" even though the
    // server has the variant. Calling /cart/add.js again would create a
    // duplicate line. Instead: if the server already has it, just re-apply a
    // fresh section to undo Dawn's stale wipe.
    enqueue(function () {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var alreadyInCart = cart.items.some(function (i) {
            return i.variant_id === variantId;
          });
          if (alreadyInCart) {
            // Server has it — DOM is stale. Re-fetch & apply the section.
            return fetch('/cart?section_id=cart-drawer', {
              credentials: 'same-origin',
            })
              .then(function (r) { return r.text(); })
              .then(function (html) { applyDrawerSection(html); });
          }
          // Genuine first add. Optimistically show the price ahead of the
          // round-trip so the toggle ON state and the total flip together.
          applyOptimistic(null, toggle);
          return cartFetch('/cart/add.js', {
            items: [{ id: variantId, quantity: qty }],
            sections: ['cart-drawer'],
            sections_url: window.location.pathname,
          }).then(function (res) {
            if (!res.ok) return;
            var html = res.body && res.body.sections && res.body.sections['cart-drawer'];
            if (html) applyDrawerSection(html);
          });
        })
        .finally(function () {
          autoAddInFlight = false;
        });
    });
  }

  // Keep the Shipping Insurance quantity in lockstep with the device count.
  //
  // The Liquid renders the toggle with data-quantity = device_count and adds
  // insurance at that qty on first add. But performShippingAutoAdd bails the
  // moment insurance is already in the cart, so when the device count later
  // CHANGES (e.g. removing one band of a 2-pack, or adding a second band) the
  // insurance line is left at its old quantity — over- or under-insuring the
  // order. Symptoms: "Your cart (3)" with 1 band + 2 insurance units, and a
  // toggle-off that only subtracts one unit's price (the optimistic math reads
  // the fresh data-quantity while the cart still holds the stale qty).
  //
  // After each drawer settle, compare the server insurance line's qty to the
  // freshly rendered desired qty and reconcile via /cart/update.js if they
  // differ. desiredQty comes from the just-rendered toggle, so it already
  // reflects the current device count.
  var shippingQtyReconcileInFlight = false;

  function reconcileShippingQuantity() {
    if (shippingQtyReconcileInFlight) return;
    var toggle = document.querySelector(
      '[data-hlth-care-toggle][data-hlth-care-type="shipping"]'
    );
    if (!toggle) return;
    // Only correct the qty when insurance is meant to be in the cart. If the
    // shopper turned it off, leave it alone — performShippingAutoAdd owns the
    // re-add decision (and would re-add at the correct qty).
    if (toggle.dataset.inCart !== 'true') return;
    var variantId = Number(toggle.dataset.variantId);
    var desiredQty = parseInt(toggle.dataset.quantity, 10) || 0;
    if (!variantId || desiredQty < 1) return;

    shippingQtyReconcileInFlight = true;
    enqueue(function () {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var line = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].variant_id === variantId) { line = cart.items[i]; break; }
          }
          if (!line) return; // not actually in cart — auto-add will handle it
          if (line.quantity === desiredQty) return; // already correct
          var updates = {};
          updates[variantId] = desiredQty;
          cartDirty = true;
          return cartFetch('/cart/update.js', {
            updates: updates,
            sections: ['cart-drawer'],
            sections_url: window.location.pathname,
          }).then(function (res) {
            if (!res.ok) return;
            var html = res.body && res.body.sections && res.body.sections['cart-drawer'];
            if (html) applyDrawerSection(html);
          });
        })
        .finally(function () {
          shippingQtyReconcileInFlight = false;
        });
    });
  }

  // If the shopper removes the last Health Tracker line via the cart-line
  // trash button (or qty 0), the +Care upsell lines are left orphaned in the
  // cart with no underlying product. They're hidden from the items list by
  // CSS, so the drawer ends up showing "Your cart (1)" with a tiny upsell
  // total and nothing the shopper can see — a confusing state. Detect that
  // and clean it up by removing every in-cart +Care variant in one
  // /cart/update.js call, then refresh the section.
  var orphanReconcileInFlight = false;

  function reconcileOrphanedCareLines() {
    if (orphanReconcileInFlight) return;

    // Two possible orphan situations now:
    //
    //  (a) Items table rendered, devices missing — older code path. Liquid
    //      currently hides the items table when device_count == 0 so this is
    //      no longer the common case, but handle it defensively for any
    //      transient render state.
    //  (b) Items table NOT rendered AND drawer shows is-empty — Liquid's
    //      cart_effectively_empty gate hid everything. Server may still hold
    //      orphan +Care lines that need clean-up. Confirm via /cart.js (the
    //      DOM has no row data we can read) before issuing the removal.
    var rows = document.querySelectorAll('.cart-item');
    var drawer = document.querySelector('cart-drawer');
    var drawerLooksEmpty = drawer && drawer.classList.contains('is-empty');

    if (rows.length > 0) {
      var hasDevice = false;
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].dataset.isDevice === 'true') {
          hasDevice = true;
          break;
        }
      }
      if (hasDevice) return;

      var toggles = document.querySelectorAll('[data-hlth-care-toggle]');
      var updates = {};
      var anyToRemove = false;
      toggles.forEach(function (t) {
        if (t.dataset.inCart === 'true' && t.dataset.variantId) {
          updates[t.dataset.variantId] = 0;
          anyToRemove = true;
        }
      });
      if (!anyToRemove) return;

      orphanReconcileInFlight = true;
      cartDirty = true;
      enqueue(function () {
        return cartFetch('/cart/update.js', {
          updates: updates,
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        })
          .then(function (res) {
            if (!res.ok) return;
            var html = res.body && res.body.sections && res.body.sections['cart-drawer'];
            if (html) applyDrawerSection(html);
          })
          .finally(function () {
            orphanReconcileInFlight = false;
          });
      });
      return;
    }

    if (!drawerLooksEmpty) return;

    // Case (b): drawer looks empty but server might hold orphans. Verify, then
    // remove non-Health-Tracker lines if found. We hit /cart.js (lightweight
    // JSON, no section render) so this is cheap on truly-empty carts too.
    orphanReconcileInFlight = true;
    enqueue(function () {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart.items || cart.items.length === 0) return; // truly empty
          // `/cart.js` is fetched WITHOUT a locale prefix, so product_type comes
          // back in the store's primary language ("Health Tracker"), NOT the
          // translated storefront value (e.g. "Terveysseuranta" on /fi/). That's
          // why comparing to the English literal is safe here even on translated
          // locales. The authoritative device check is the tag-based
          // `cart_effectively_empty` gate in cart-drawer.liquid (keyed on the
          // locale-stable `Wearable` tag) — this is only a redundant guard, and
          // it errs safe: a false "has device" just skips the destructive cleanup.
          var hasDevice = cart.items.some(function (it) {
            return it.product_type === 'Health Tracker';
          });
          if (hasDevice) return; // shouldn't happen given drawerLooksEmpty, but be safe
          var updates = {};
          cart.items.forEach(function (it) { updates[it.variant_id] = 0; });
          cartDirty = true;
          return cartFetch('/cart/update.js', {
            updates: updates,
            sections: ['cart-drawer'],
            sections_url: window.location.pathname,
          }).then(function (res) {
            if (!res.ok) return;
            var html = res.body && res.body.sections && res.body.sections['cart-drawer'];
            if (html) applyDrawerSection(html);
          });
        })
        .finally(function () {
          orphanReconcileInFlight = false;
        });
    });
  }

  // Debounced trigger. Dawn renders the cart-drawer in two stages around an
  // ATC: a synchronous `renderContents` swap on #CartDrawer, then an async
  // `onCartUpdate` that fetches /cart?section_id=cart-drawer and replaces
  // `cart-drawer-items` + `.cart-drawer__footer`. If we auto-add too early,
  // the second swap wipes our changes. Waiting ~250ms of "no further drawer
  // mutations" lets both stages settle before we act.
  var stableTimer = null;
  function scheduleStableCheck() {
    if (stableTimer) clearTimeout(stableTimer);
    stableTimer = setTimeout(function () {
      stableTimer = null;
      // Order matters: reconcile orphans first so a "+Care lines but no
      // device" cart gets cleaned up before we'd otherwise auto-add another
      // shipping line on top. reconcile bails when a device is present, and
      // the auto-add bails when the toggle is already in cart, so they don't
      // step on each other.
      reconcileOrphanedCareLines();
      performShippingAutoAdd();
      // Then fix the insurance qty if the device count changed while it was
      // already in the cart (auto-add bails in that case).
      reconcileShippingQuantity();
    }, 250);
  }

  // Keep `cart-drawer.is-empty` in sync with the presence of the Liquid empty
  // block (`.drawer__inner-empty`). Dawn's CartDrawerItems.updateQuantity sets
  // is-empty based on `parsedState.item_count === 0`, but in the orphan state
  // (only +Care lines remain) item_count is 1+, so Dawn leaves is-empty OFF
  // while our Liquid `cart_effectively_empty` gate has already rendered the
  // empty-state markup. Without is-empty, the `cart-drawer.is-empty .drawer__inner`
  // grid layout and `.drawer__header { display:none }` rule never activate —
  // resulting in a blank drawer with the header floating at the bottom. This
  // runs synchronously on every drawer mutation so the class flips the same
  // tick the markup does, no visible flash.
  function syncCartDrawerEmptyClass() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var hasEmptyMarkup = !!drawer.querySelector('.drawer__inner-empty');
    drawer.classList.toggle('is-empty', hasEmptyMarkup);
  }

  function installDrawerObserver() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    syncCartDrawerEmptyClass();
    // Initial run handles the page-load case where the cart already has items
    // (e.g. user refreshes mid-checkout) without needing a mutation to kick it.
    scheduleStableCheck();
    new MutationObserver(function () {
      // Fast path first: keep the empty-state class consistent with the rendered
      // markup right now, before any debounced checks fire.
      syncCartDrawerEmptyClass();
      scheduleStableCheck();
    }).observe(drawer, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installDrawerObserver);
  } else {
    installDrawerObserver();
  }

  document.addEventListener('change', function (event) {
    var toggle = event.target && event.target.closest
      ? event.target.closest(TOGGLE_SEL)
      : null;
    if (!toggle) return;

    var nowChecked = toggle.checked;

    // Each toggle is independent (no radio behavior). Update UI optimistically
    // based on this toggle's delta only.
    applyOptimistic(
      nowChecked ? null : toggle,  // prev (being removed) = this toggle when turning OFF
      nowChecked ? toggle : null,  // next (being added)   = this toggle when turning ON
    );

    enqueue(function () { return syncToggleToCart(toggle); });
  });

  // ── Learn-more popup ──────────────────────────────────────────────────
  function openPopup(triggerBtn) {
    var popup = document.querySelector('[data-hlth-care-popup]');
    if (!popup) return;
    var heading = triggerBtn.getAttribute('data-popup-heading') || '';
    var body = triggerBtn.getAttribute('data-popup-body') || '';
    var headingEl = popup.querySelector('[data-hlth-care-popup-heading]');
    var bodyEl = popup.querySelector('[data-hlth-care-popup-body]');
    if (headingEl) headingEl.textContent = heading;
    if (bodyEl) bodyEl.textContent = body;
    popup.removeAttribute('hidden');
    document.body.classList.add('hlth-care-popup-open');
    var closeBtn = popup.querySelector('[data-hlth-care-popup-close]');
    if (closeBtn && closeBtn.focus) closeBtn.focus();
  }

  function closePopup() {
    var popup = document.querySelector('[data-hlth-care-popup]');
    if (!popup) return;
    popup.setAttribute('hidden', '');
    document.body.classList.remove('hlth-care-popup-open');
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var trigger = target.closest('[data-hlth-care-learn-more]');
    if (trigger) {
      event.preventDefault();
      openPopup(trigger);
      return;
    }
    if (target.closest('[data-hlth-care-popup-close]')) {
      event.preventDefault();
      closePopup();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var popup = document.querySelector('[data-hlth-care-popup]');
    if (popup && !popup.hasAttribute('hidden')) closePopup();
  });
})();
